import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import moment from 'moment';
import { HdContentLanguageService } from '../../chart/bodygraph/hd-content-language.service';
import { momentLocaleFor } from '@shared/utils/moment-locale.utils';
import { HdTransitLineDay, hdTransitAddDays } from './hd-transit-day-flow.builder';
import { HdTransitSunLineComponent } from './hd-transit-sun-line.component';

const I18N_PREFIX = 'humanDesign.chart.transits.vertical.weekStrip';
const DAY_KEY_FORMAT = 'YYYY-MM-DD';

interface WeekStripDayVm {
  readonly key: string;
  readonly weekday: string;
  readonly dayNumber: number;
  readonly isToday: boolean;
  readonly isFocus: boolean;
  /** Lijn-dag van de zon; `null` zolang de data er niet is. */
  readonly sunLine: HdTransitLineDay | null;
}

/**
 * Weekstrip bovenaan de verticale transits-weergave (spec §2.3): de zeven
 * dagen (ma-zo) van de week van de focusdag, met vandaag en de focusdag
 * gemarkeerd. Werkt als "scrollbalk" van de oneindige dag-flow: een klik op
 * een dag, de weekpijlen of "Vandaag" laten de flow naar die dag springen,
 * en de focusdag volgt het scrollen van de flow. Dom component: de host
 * houdt de focusdag bij. Rechtsboven in elke tegel de lijn-dag van de zon
 * (`sunLines`, van de host).
 */
@Component({
  selector: 'hd-transit-week-strip',
  imports: [HdTransitSunLineComponent],
  templateUrl: './hd-transit-week-strip.component.html',
  styleUrl: './hd-transit-week-strip.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdTransitWeekStripComponent {
  /** Lokale dagsleutel (`YYYY-MM-DD`) van de dag bovenaan de dag-flow. */
  readonly focusDayKey = input.required<string>();
  readonly todayKey = input.required<string>();
  /** Compacte maten voor het smalle dockpaneel. */
  readonly compact = input(false);
  /** Kalenderknop tonen (smalle weergave: de maandkalender klapt dan open onder de strip). */
  readonly calendarToggle = input(false);
  readonly calendarOpen = input(false);
  /** Lijn-dag van de zon per lokale dagsleutel; een dag zonder (volledige) data toont niets. */
  readonly sunLines = input<ReadonlyMap<string, HdTransitLineDay> | null>(null);

  readonly daySelected = output<string>();
  /** Nu-knop: de host laat de flow naar de nu-lijn springen. */
  readonly nowSelected = output<void>();
  readonly calendarToggled = output<void>();

  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  protected readonly text = computed(() => {
    this.contentLang.translationsLoadedTick();
    const lang = this.contentLang.effectiveLang();
    const translate = (key: string) => this.transloco.translate<string>(`${I18N_PREFIX}.${key}`, undefined, lang);
    return {
      previousWeek: translate('previousWeek'),
      nextWeek: translate('nextWeek'),
      today: translate('today'),
      now: translate('now'),
      nowTooltip: translate('nowTooltip'),
      showCalendar: translate('showCalendar'),
      hideCalendar: translate('hideCalendar'),
    };
  });

  /** Moment-locale van de content-taal (de globale volgt een taalwissel niet). */
  private readonly locale = computed(() => momentLocaleFor(this.contentLang.effectiveLang()));

  protected readonly days = computed<readonly WeekStripDayVm[]>(() => {
    const focusKey = this.focusDayKey();
    const todayKey = this.todayKey();
    const sunLines = this.sunLines();
    const monday = moment(focusKey, DAY_KEY_FORMAT).locale(this.locale()).startOf('isoWeek');
    return Array.from({ length: 7 }, (_, index) => {
      const day = monday.clone().add(index, 'day');
      const key = day.format(DAY_KEY_FORMAT);
      return {
        key,
        weekday: day.format('dd'),
        dayNumber: day.date(),
        isToday: key === todayKey,
        isFocus: key === focusKey,
        sunLine: sunLines?.get(key) ?? null,
      };
    });
  });

  /** "september 2026"; een week over twee maanden toont beide ("sep – okt 2026"). */
  protected readonly monthLabel = computed(() => {
    const days = this.days();
    const locale = this.locale();
    const first = moment(days[0].key, DAY_KEY_FORMAT).locale(locale);
    const last = moment(days[days.length - 1].key, DAY_KEY_FORMAT).locale(locale);
    if (first.month() === last.month()) return first.format('MMMM YYYY');
    return first.year() === last.year()
      ? `${first.format('MMM')} – ${last.format('MMM YYYY')}`
      : `${first.format('MMM YYYY')} – ${last.format('MMM YYYY')}`;
  });

  protected shiftWeek(delta: number): void {
    this.daySelected.emit(hdTransitAddDays(this.focusDayKey(), delta * 7));
  }

  protected goToToday(): void {
    this.daySelected.emit(this.todayKey());
  }
}
